import {
  formatInstant,
  fromApiMoney,
  type LocalDate,
  type Money,
  startOfDayUtc,
  toApiMoney,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey, principalKey } from '../http/keys.ts'

export type TransferStatus = Schemas['LedgerTransferResponse']['status']

export interface Transfer {
  readonly id: string
  readonly sourceLedgerId: string
  readonly targetLedgerId: string
  readonly status: TransferStatus
  readonly source: Money
  readonly target: Money
}

export interface ProposeTransferInput {
  readonly sourceAccountId: string
  readonly source: Money
  readonly targetLedgerId: string
  readonly target: Money
  readonly date: LocalDate
  /** Known up front, both legs post immediately instead of waiting on an accept. */
  readonly targetAccountId?: string
  readonly memo?: string
  readonly settlesObligationId?: string
}

export interface CrossLedgerExpenseInput {
  readonly expenseAccountId: string
  readonly expense: Money
  readonly payerLedgerId: string
  readonly payerAccountId: string
  readonly payer: Money
  readonly date: LocalDate
  readonly memo?: string
}

export interface CrossLedgerExpenseResult {
  readonly obligationId: string | undefined
  readonly debtorEntryId: string
  readonly payerEntryId: string
}

export const transferKeys = {
  inbox: (): CacheKey => principalKey('transfers', 'inbox'),
  detail: (ledgerId: string, transferId: string): CacheKey =>
    ledgerKey(ledgerId, 'transfers', 'detail', transferId),
  /** A transfer's lifecycle touches both ledgers' journals and everyone's inbox. */
  invalidatedByWrite: (sourceLedgerId: string, targetLedgerId: string): Invalidations => [
    principalKey('transfers'),
    ledgerKey(sourceLedgerId, 'entries'),
    ledgerKey(targetLedgerId, 'entries'),
  ],
} as const

export interface TransfersRepository {
  get(ledgerId: string, transferId: string, signal?: AbortSignal): Promise<Transfer>
  create(ledgerId: string, input: ProposeTransferInput): Promise<Transfer>
  inbox(signal?: AbortSignal): Promise<readonly Transfer[]>
  accept(ledgerId: string, transferId: string, targetAccountId: string): Promise<Transfer>
  /** `204 No Content` on the wire - the contract carries no updated transfer back. */
  reject(ledgerId: string, transferId: string): Promise<void>
  reverse(ledgerId: string, transferId: string): Promise<Transfer>
  crossLedgerExpenses(ledgerId: string, input: CrossLedgerExpenseInput): Promise<CrossLedgerExpenseResult>
}

/** `toApiMoney`'s `currency: string` already carries a `Money`-validated `CurrencyCode`; only the generated schema's literal union needs the cast. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

export function createTransfersRepository(client: TalerClient): TransfersRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/transfers`
  const one = (ledgerId: string, transferId: string) => `${base(ledgerId)}/${encodeURIComponent(transferId)}`

  return {
    async get(ledgerId, transferId, signal) {
      return toTransfer(
        await client.call<Schemas['LedgerTransferResponse']>({
          method: 'GET',
          path: one(ledgerId, transferId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      const body: Schemas['ProposeTransferRequest'] = {
        sourceAccountId: input.sourceAccountId,
        source: apiMoney(input.source),
        targetLedgerId: input.targetLedgerId,
        target: apiMoney(input.target),
        date: formatInstant(startOfDayUtc(input.date)),
        ...(input.targetAccountId === undefined ? {} : { targetAccountId: input.targetAccountId }),
        ...(input.memo === undefined ? {} : { memo: input.memo }),
        ...(input.settlesObligationId === undefined
          ? {}
          : { settlesObligationId: input.settlesObligationId }),
      }
      return toTransfer(
        await client.call<Schemas['LedgerTransferResponse'], Schemas['ProposeTransferRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body,
        }),
      )
    },

    async inbox(signal) {
      const response = await client.call<Schemas['LedgerTransferResponse'][]>({
        method: 'GET',
        path: '/api/v1/transfers/inbox',
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toTransfer)
    },

    async accept(ledgerId, transferId, targetAccountId) {
      return toTransfer(
        await client.call<Schemas['LedgerTransferResponse'], Schemas['AcceptTransferRequest']>({
          method: 'POST',
          path: `${one(ledgerId, transferId)}/accept`,
          body: { targetAccountId },
        }),
      )
    },

    async reject(ledgerId, transferId) {
      await client.call<void, Record<string, never>>({
        method: 'POST',
        path: `${one(ledgerId, transferId)}/reject`,
        body: {},
      })
    },

    async reverse(ledgerId, transferId) {
      return toTransfer(
        await client.call<Schemas['LedgerTransferResponse'], Record<string, never>>({
          method: 'POST',
          path: `${one(ledgerId, transferId)}/reverse`,
          body: {},
        }),
      )
    },

    async crossLedgerExpenses(ledgerId, input) {
      const body: Schemas['PostCrossLedgerExpenseRequest'] = {
        expenseAccountId: input.expenseAccountId,
        expense: apiMoney(input.expense),
        payerLedgerId: input.payerLedgerId,
        payerAccountId: input.payerAccountId,
        payer: apiMoney(input.payer),
        date: formatInstant(startOfDayUtc(input.date)),
        ...(input.memo === undefined ? {} : { memo: input.memo }),
      }
      const response = await client.call<
        Schemas['CrossLedgerExpenseResponse'],
        Schemas['PostCrossLedgerExpenseRequest']
      >({
        method: 'POST',
        path: `${base(ledgerId)}/cross-ledger-expenses`,
        body,
      })
      return {
        obligationId: response.obligationId,
        debtorEntryId: response.debtorEntryId,
        payerEntryId: response.payerEntryId,
      }
    },
  }
}

function toTransfer(response: Schemas['LedgerTransferResponse']): Transfer {
  return {
    id: response.id,
    sourceLedgerId: response.sourceLedgerId,
    targetLedgerId: response.targetLedgerId,
    status: response.status,
    source: fromApiMoney(response.source),
    target: fromApiMoney(response.target),
  }
}
